import React, { memo, type ComponentType } from 'react';
import { graphql, usePreloadedQuery, type PreloadedQuery } from 'react-relay';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import LinkMenuItem from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/ui/link-menu-item-with-analytics/index.tsx';
import { Skeleton } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-settings-global-common/src/ui/skeleton/index.tsx';
import type { GlobalProductsSettingsSidebarQuery } from '@atlassian/jira-relay/src/__generated__/GlobalProductsSettingsSidebarQuery.graphql.ts';
import { COMMON_ANALYTICS_ATTRIBUTES } from '../common/constants.tsx';
import { useFeatures } from '../controllers/features/index.tsx';
import IntegrationsSection from './integrations-section/index.tsx';
import JiraSection from './jira-section/index.tsx';
import messages from './messages.tsx';
import OperationsSection from './operations-section/index.tsx';
import PlansSection from './plans-section/index.tsx';
import ProductDiscoverySection from './product-discovery-section/index.tsx';
import ServiceManagementSection from './service-management-section/index.tsx';
import CustomerServiceSection from './customer-service-section/index.tsx';
import { DelegatedAdminJiraSection } from './jira-section/DelegatedAdminJiraSection.tsx';

type Props = {
	queryReference: PreloadedQuery<GlobalProductsSettingsSidebarQuery>;
};

// When the FG `jira-delegated-custom-onboarding`is disabled, this component will be
// replaced by the component in main.tsx, so if you are making changes here, please also
// make them there.
const GlobalProductsSettingsSidebarInner = ({ queryReference }: Props) => {
	const productAccessItemAppify = fg('product-terminology-refresh')
		? messages.productAccessItemAppify
		: messages.productAccessItem;
	const { formatMessage } = useIntl();
	const { data: features } = useFeatures();

	const queryData = usePreloadedQuery<GlobalProductsSettingsSidebarQuery>(
		graphql`
			query GlobalProductsSettingsSidebarQuery($cloudId: ID!, $skipDelegatedAdminCheck: Boolean!)
			@preloadable {
				...DelegatedAdminJiraSection
					@arguments(cloudId: $cloudId, skipDelegatedAdminCheck: $skipDelegatedAdminCheck)
			}
		`,
		queryReference,
	);

	if (!features) {
		return (
			<>
				<Skeleton />
				<UFOLoadHold name="products-data-loading" />
			</>
		);
	}

	return (
		<>
			{features.hasApplicationAccess && (
				<LinkMenuItem
					analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'productAccess' }}
					href="/secure/admin/ApplicationAccess.jspa"
					target="_blank"
					elemAfter={<LinkExternalIcon spacing="none" label="" color={token('color.icon')} />}
				>
					{formatMessage(productAccessItemAppify)}
				</LinkMenuItem>
			)}
			{features.hasSoftwareSection && features.hasSoftwareConfig && <JiraSection />}
			{/*
			Only show the DelegatedAdminJiraSection if the JiraSection isn't already being shown.
			*/}
			{!(features.hasSoftwareSection && features.hasSoftwareConfig) && (
				<DelegatedAdminJiraSection queryReference={queryData} />
			)}
			{features.hasPlansSection && <PlansSection />}
			{features.hasServiceManagementSection && <ServiceManagementSection features={features} />}
			{features.hasCustomerServiceSection && <CustomerServiceSection />}
			{features.hasOperationsSection && <OperationsSection features={features} />}
			{features.hasProductDiscoverySection && <ProductDiscoverySection />}
			{features.hasIntegrationsSection && <IntegrationsSection features={features} />}
		</>
	);
};

export const GlobalProductsSettingsSidebar: ComponentType<Props> = memo(
	GlobalProductsSettingsSidebarInner,
);
